// ---------------------------------------------------------------------------
// Centralized semantic status palette for admin surfaces.
// Single source of truth for status badge / accent colors used across
// client-orders, producer-orders, and future entity lifecycles.
//
// Why it exists:
//   The MUI theme enables CSS-variables mode with `data-color-scheme` switching,
//   but only `text.*`, `background.*`, and `action.*` are mode-aware here —
//   colored ramps (`error.lighter`, `info.main`, …) resolve to the same hex in
//   light and dark mode (see `src/theme/core/palette.ts`). Admin status surfaces
//   (badges, urgency pills, row tints) need per-mode hex, so they can't rely on
//   MUI's colored ramps directly. This palette fills that gap.
//
// Documentation:
//   • Design reference — DESIGN-LANGUAGE.md §4
//   • Implementation reference — STATUS-PALETTE.md
// ---------------------------------------------------------------------------

import { alpha } from '@mui/material/styles';

export type StatusColorSet = {
  /** Low-saturation pill / card fill. */
  bg: string;
  /** Text color on top of `bg`. */
  color: string;
  /** Saturated color for borders, focus rings, icon-only indicators. */
  accent: string;
  /** Low-alpha wash for row-level highlights (tints a whole table row). */
  rowBg: string;
};

export type StatusColorPair = {
  light: StatusColorSet;
  dark: StatusColorSet;
};

export type StatusToken = 'neutral' | 'info' | 'warning' | 'success' | 'complete' | 'error';

// Build a StatusColorPair, applying seed rules for `accent` and `rowBg` when
// the caller doesn't supply explicit overrides.
//
// Seed rules:
//   • accent = color           (text color doubles as saturated accent — same hue family)
//   • rowBg  = alpha(bg, 0.5)  for light
//   • rowBg  = alpha(bg, 0.3)  for dark
//
// Callers pass explicit `accent` / `rowBg` only when the seed rule is wrong
// for that token (see `error` below).
type SideOverrides = { bg: string; color: string; accent?: string; rowBg?: string };

function createPair(light: SideOverrides, dark: SideOverrides): StatusColorPair {
  return {
    light: {
      bg: light.bg,
      color: light.color,
      accent: light.accent ?? light.color,
      rowBg: light.rowBg ?? alpha(light.bg, 0.5),
    },
    dark: {
      bg: dark.bg,
      color: dark.color,
      accent: dark.accent ?? dark.color,
      rowBg: dark.rowBg ?? alpha(dark.bg, 0.3),
    },
  };
}

// `error` overrides (preserved from NAT-1730's STATUS_ACCENT for zero visual change):
//   • light.accent = '#E24B4A' — distinct from text color `#A32D2D` (more saturated).
//   • dark.accent  = '#F09595' — distinct from text color `#F4A8A8`.
//   • dark.rowBg   = 'rgba(80, 19, 19, 0.3)' — legacy off-palette (matches
//                    shipping-countdown's URGENCY_DARK_OVERRIDES.error.bg at α=0.3,
//                    NOT alpha(error.dark.bg, 0.3) = '#5C1A1A'). Brand refresh will
//                    reconcile.
export const STATUS_PALETTE: Record<StatusToken, StatusColorPair> = {
  neutral: createPair({ bg: '#F1EFE8', color: '#5F5E5A' }, { bg: '#444441', color: '#D3D1C7' }),
  info: createPair({ bg: '#E6F1FB', color: '#185FA5' }, { bg: '#0C447C', color: '#B5D4F4' }),
  warning: createPair({ bg: '#FAEEDA', color: '#854F0B' }, { bg: '#633806', color: '#FAC775' }),
  success: createPair({ bg: '#E1F5EE', color: '#0F6E56' }, { bg: '#085041', color: '#9FE1CB' }),
  complete: createPair({ bg: '#EAF3DE', color: '#3B6D11' }, { bg: '#27500A', color: '#C0DD97' }),
  error: createPair(
    { bg: '#FCEBEB', color: '#A32D2D', accent: '#E24B4A' },
    { bg: '#5C1A1A', color: '#F4A8A8', accent: '#F09595', rowBg: 'rgba(80, 19, 19, 0.3)' },
  ),
};

// ---------------------------------------------------------------------------
// Entity status mappings.
// Each entity with a lifecycle declares its enum → token map here.
// See STATUS-PALETTE.md §4 for the extension recipe.
// ---------------------------------------------------------------------------

export type ClientOrderStatus = 'inserito' | 'inoltrato' | 'pronto' | 'spedito' | 'consegnato';

export const CLIENT_ORDER_STATUS_MAP: Record<ClientOrderStatus, StatusToken> = {
  inserito: 'neutral',
  inoltrato: 'info',
  pronto: 'warning',
  spedito: 'success',
  consegnato: 'complete',
};

export type ProducerOrderStatus =
  | 'bozza'
  | 'inviato'
  | 'confermato'
  | 'parz_ricevuto'
  | 'ricevuto'
  | 'annullato';

export const PRODUCER_ORDER_STATUS_MAP: Record<ProducerOrderStatus, StatusToken> = {
  bozza: 'neutral',
  inviato: 'info',
  confermato: 'success',
  parz_ricevuto: 'warning',
  ricevuto: 'complete',
  annullato: 'error',
};
